import { useEffect, useState } from 'react';
import { NativeModules, Platform } from 'react-native';
import { getUserAgent } from 'react-native-device-info';
import { googlePalService, NonceManagerReturn } from 'react-native-google-pal';
import { useQuery } from 'react-query';
import axios from 'axios';
import { useRecoilState } from 'recoil';
import { MINUTE, SECOND } from 'time-constants';

import { apiConfig, CURRENT_LIVE_VIDEO_STREAM, getPalRequest } from '@constants';
import { helpers } from '@helpers';
import { liveVideoPlayerState } from '@store';

import app from '../../../app.json';
import { addLiveRampSegmentAttribution } from '../../helpers/addLiveRampSegmentAttribution';
import { getDspAccessToken } from '../../helpers/DspAuth';
import { isFireTV, isTizen, isVizio, isWeb, isX1 } from '../../helpers/Platform';
import { refetchIntervalByExpirationTime } from '../../helpers/refetchIntervalUtils';
import { getStorageItem, setStorageItem } from '../../helpers/storage';
import { CurrentLiveStreamType } from '../../store/types/LiveVideoPlayerStateType';
import { getLiveRamp } from '../useLiveRamp/helpers';
import { liveVideoStreamTypes } from '../useLiveStreamSelector';

const getLiveVideo = async (retry = false, currentLiveVideoStream: CurrentLiveStreamType) => {
  const { accessToken, error } = await getDspAccessToken(retry);

  if (error) {
    return error;
  }

  let storedValue = currentLiveVideoStream;

  if (!storedValue) {
    storedValue = getStorageItem(CURRENT_LIVE_VIDEO_STREAM);
    if (!storedValue) {
      setStorageItem(CURRENT_LIVE_VIDEO_STREAM, liveVideoStreamTypes.videoStream);
      storedValue = liveVideoStreamTypes.videoStream;
    }
  }

  const videoStreamUrls = {
    [liveVideoStreamTypes.videoStream]: apiConfig.LIVE_VIDEO_STREAM_URL,
    [liveVideoStreamTypes.videoStreamSpanish]: apiConfig.LIVE_VIDEO_STREAM_SPANISH_URL,
    [liveVideoStreamTypes.videoStreamPattrn]: apiConfig.LIVE_VIDEO_STREAM_PATTRN_URL,
  };

  const selectedLiveStreamUrl = videoStreamUrls[storedValue];

  const liveRamp = getLiveRamp();

  const updatedWithParameters = await updateWithAdParameters(selectedLiveStreamUrl);

  const urlWithParameters = liveRamp?.envelope
    ? addLiveRampSegmentAttribution(updatedWithParameters, liveRamp.envelope)
    : updatedWithParameters;

  // end previous playback
  await googlePalService.sendPlaybackEnd();
  const nonceResponse = await googlePalService.getNonceManager(getPalRequest());

  const { data } = await axios
    .get(urlWithParameters, {
      headers: {
        'x-access-token': accessToken,
      },
    })
    .catch(() => {
      console.log('in catch video error');
      return retry ? { data: { error: 'access error. Token expired?' } } : getLiveVideo(true, currentLiveVideoStream);
    });

  const { data: scheduleData } = await axios.get(`${apiConfig.LIVE_CHANNEL_INFO_URL}`, {
    headers: {
      'x-access-token': accessToken,
    },
  });

  if (!data || data?.error) {
    return scheduleData?.channels?.length
      ? {
          schedule: getFormattedScheduleData(scheduleData, storedValue),
          isLive: true,
        }
      : data;
  }

  const userAgentString = await getUserAgentParameter();

  return formatData(data, scheduleData, userAgentString, storedValue, nonceResponse);
};

const getX1AdID = async () => {
  const getID = new Promise((resolve, reject) => {
    window.$badger
      .info()
      .success(data => {
        resolve(data?.receiverId ?? data?.deviceHash);
      })
      .failure(error => {
        console.warn(error);
        reject(error);
      });
  });

  try {
    return await getID;
  } catch (error) {
    console.warn('unable to access x1 device ad id');
    return '';
  }
};

const getX1lmt = async defaultValue => {
  const getlmt = new Promise((resolve, reject) => {
    window.$badger
      .info()
      .success(data => {
        resolve(data?.privacySettings.lmt);
      })
      .failure(error => {
        console.warn(error);
        reject(error);
      });
  });

  try {
    return await getlmt;
  } catch (error) {
    console.warn('unable to access x1 device lmt value');
    return defaultValue;
  }
};

const getX1Model = async () => {
  const getModel = new Promise((resolve, reject) => {
    window.$badger
      .deviceCapabilities()
      .success(data => {
        resolve(data?.deviceType ?? '');
      })
      .failure(error => {
        console.warn(error);
        reject(error);
      });
  });

  try {
    return await getModel;
  } catch (error) {
    console.warn('unable to access x1 device model');
    return '';
  }
};

const updateWithAdParameters = async url => {
  let appBundle = '';
  let adID = '';
  let adIDType = '';
  let limitAdTracking = 1;
  let deviceType = '';
  let deviceMake = '';
  let deviceModel = '';

  if (isWeb) {
    if (isX1) {
      appBundle = 'the_weather_group_the_weather_channel';
      adID = await getX1AdID();
      limitAdTracking = await getX1lmt(limitAdTracking);
      adIDType = 'dpid';
      deviceMake = 'Comcast';
      deviceType = 'comcastx1';
      deviceModel = await getX1Model();
    } else if (isTizen) {
      appBundle = 'G15055001404';
      adIDType = 'tifa';
      deviceMake = 'Samsung';
      deviceType = 'tizen';

      try {
        limitAdTracking = window.webapis?.adinfo?.isLATEnabled() ? 1 : 0;
      } catch (error) {
        console.warn('unable to access tizen lmt value');
        console.warn(error);
      }

      try {
        adID = window.webapis?.adinfo?.getTIFA();
      } catch (error) {
        console.warn('unable to access tizen device ad ID');
        console.warn(error);
      }

      try {
        deviceModel = window.webapis?.productinfo?.getRealModel();
      } catch (error) {
        console.warn('unable to access tizen device model');
      }
    } else if (isVizio) {
      appBundle = apiConfig.VIZIO_APP_BUNDLE;
      adID = window.VIZIO.adID.IFA;
      limitAdTracking = window.VIZIO.adID.LMT;
      adIDType = window.VIZIO.adID.IFA_TYPE;
      deviceMake = 'Vizio';
      deviceType = 'vizio';
      deviceModel = window.VIZIO.deviceModel;
    }
  } else {
    const { AdvertisingIDModule } = NativeModules;

    adID = AdvertisingIDModule.getAdvertisingInfoID();
    limitAdTracking = AdvertisingIDModule.getAdvertisingInfoLimitAdTracking() ? 1 : 0;
    adIDType = isFireTV ? 'afai' : 'aaid';
    appBundle = isFireTV ? 'B07PNRRTN6' : 'com.weathergroup.twc';
    deviceType = isFireTV ? 'fire_tv' : 'Android';
    deviceMake = isFireTV ? 'Amazon' : 'Google';
    deviceModel = Platform.constants.Model;
  }

  url = `${url}?app_name=${app.name}&app_bundle=${appBundle}&device_make=${deviceMake}&device_model=${deviceModel}&device_type=${deviceType}&dnt=${limitAdTracking}&device_ifa=${adID}&device_id_type=${adIDType}&rtb_device=3`;
  return url;
};

const formatData = (liveData, scheduleData, userAgent, currentLiveVideoStream, nonce: NonceManagerReturn) => {
  const dspURL = liveData.video_m3u8;
  const dspParameters = helpers.getParamsFromUrl(dspURL);
  const url = helpers.getBaseURL(dspURL);
  const mappedAdsParameters = {};

  if (userAgent) {
    mappedAdsParameters['ads.ua'] = userAgent;
    console.log('setting live UA: ');
    console.log(userAgent);
  }

  let allAdsParameters = { ...dspParameters, ...mappedAdsParameters };

  if (nonce.success) {
    allAdsParameters = { ...allAdsParameters, 'ads.paln': nonce.nonce };
  }

  return scheduleData?.channels?.length
    ? {
        url: encodeURI(helpers.attachParams(url, allAdsParameters)),
        isLive: true,
        ...liveData,
        schedule: getFormattedScheduleData(scheduleData, currentLiveVideoStream),
      }
    : {
        url: encodeURI(helpers.attachParams(url, allAdsParameters)),
        isLive: true,
        ...liveData,
      };
};

const getFormattedScheduleData = (scheduleData, currentLiveVideoStream: CurrentLiveStreamType) => {
  let selectedChannel =
    currentLiveVideoStream === liveVideoStreamTypes.videoStreamSpanish ? 'epg-twc-espanol' : 'epg-twc-prod';

  if (scheduleData.channels.length > 0) {
    const scheduleChannel = scheduleData.channels.find(channel => channel.slug === selectedChannel);

    if (!scheduleChannel) {
      console.warn(`Did not find selected stream by slug: ${selectedChannel}`);
      selectedChannel = scheduleData.channels[0].slug;
      console.warn(`setting new selected channel: ${selectedChannel}`);
    }
  }

  return scheduleData.channels.map(channel => ({
    ...channel,
    selected: channel.slug === selectedChannel,
  }));
};

const getUserAgentParameter = async () => (isWeb ? navigator.userAgent : await getUserAgent());

export const useGetLiveVideo = () => {
  const [liveVideoState, setLiveVideoState] = useRecoilState(liveVideoPlayerState);
  const [refetchInterval, setRefetchInterval] = useState(5 * MINUTE);

  const { data, error, isLoading, isFetched, refetch } = useQuery(
    `getLiveVideo-${liveVideoState.currentLiveVideoStream}`,
    () => getLiveVideo(false, liveVideoState.currentLiveVideoStream),
    {
      enabled: !liveVideoState.stopped,
      refetchOnWindowFocus: false,
      refetchInterval,
      retry: false,
      onSettled: (_data, _error) => {
        if (_error || !_data || _data?.error) {
          setRefetchInterval(10 * SECOND);
        } else if (_data.schedule) {
          const programs = _data.schedule.find(channel => channel.selected)?.program;
          const now = Date.now() / SECOND;
          const currentProgram = programs?.find(({ starts_at, ends_at }) => starts_at <= now && ends_at > now);

          if (currentProgram) {
            refetchIntervalByExpirationTime({
              expirationTimeUtc: currentProgram.ends_at,
              setRefetchInterval,
            });
          } else {
            setRefetchInterval(5 * MINUTE);
          }
        } else {
          setRefetchInterval(5 * MINUTE);
        }
      },
    },
  );

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveVideoState.currentLiveVideoStream]);

  useEffect(() => {
    setLiveVideoState(currentState => ({
      ...currentState,
      data,
      error,
      isFetched,
      isLoading,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetched]);

  return liveVideoState;
};
